import { Fragment, useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
//services
import {
	deleteBeneficiary,
	getPaymentsOptions,
	validateStep2,
	getPaymentCategories,
} from 'services/beneficiary.services';
//reducers
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import {
	setBeneficiary,
	getNewBeneficiary,
	getMessage,
	setMessage,
	setActiveStep,
	ifPaymentOptional,
	getPaymentOptions,
	setOptionalPayments,
	upsertBeneficiary,
	setPaymentCategoryOptions,
	getPaymentCategoryOptions,
} from 'state/reducers/beneficiary.reducers';
//components
import { invalidFields } from 'components/FormCreator/types';
import { setListOfInvalids, validateFields } from 'components/FormCreator/validation';
import WarningDialog from 'components/WarningDialog';
//sections
import LocalPaymentForm from './LocalPaymentForm';
import BankAccountDetailsForm from './BankAccountDetailsForm';
import Footer from '../Footer';
import FormWrap from './FormWrap';
//constants
import {
	Beneficiary,
	paymentsTypes,
	beneficiarySteps,
	BeneficiaryDetailsBankAccount,
	fieldsValidations,
	paymentsMainTypes,
	paymentsMethodsMapping,
	formalBankApproveFileValidation,
	BeneficiaryStatus,
	commentForUnequalCountriesValidation,
} from 'constants/beneficiary.constants';
import { userMessage, userMesaageType } from 'constants/ui.constants';
import { prepareBeneficiaryForCreation } from '../../helper';
import { FormsStack } from 'theme/elements/Forms';
import { Checkbox, Stack, Typography } from '@mui/material';
import { FormHeadWithCheckbox } from 'theme/elements/Headers';
import { UserPermission } from 'constants/user.constants';
import { InfoCard } from 'theme/elements/Cards';
import { CustomCheckbox } from 'theme/elements/Inputs';
import { getUserPermission } from 'state/reducers/user.reducers';
import NotesForm from './NotesForm';
import TaxShelterForm from './TaxShelterForm';
import { getCountriesObj } from 'state/reducers/global.reducers';
import { useLocation } from 'react-router-dom';
import { MultiPaymentCategoryPicker } from 'components/PaymentCategoryPicker';

export default function Step2(props: {
	handleCancel: any;
	isPaymentOptionsPreloaded: boolean;
	isCountryChanged: any;
	setIsCountryChanged: any;
}) {
	const { isPaymentOptionsPreloaded } = props;
	const t = useAppSelector(selectTranslations);
	const beneficiary: Beneficiary = useAppSelector(getNewBeneficiary);
	const userMessage: userMessage = useAppSelector(getMessage);
	const isOptional: boolean = useAppSelector(ifPaymentOptional);
	const paymentOptions = useAppSelector(getPaymentOptions);
	const prefixFields = 'page.beneficiary.create.step.2.';
	const dispatch = useAppDispatch();
	const userPermission = useAppSelector(getUserPermission);
	const countriesObj = useAppSelector(getCountriesObj);
	const categoryOptions = useAppSelector(getPaymentCategoryOptions);
	const location = useLocation();
	const paths = location.pathname.split('/');
	const lastPath = paths[paths.length - 1];
	const isNewBeneficiary = useMemo(() => _.isEqual(lastPath, 'new'), [lastPath]);

	const openDialog = useMemo(() => {
		return userMessage.exist;
	}, [userMessage]);
	const needFormalBankApprove = useMemo(
		() =>
			Boolean(_.get(countriesObj, beneficiary?.countryId || 0)?.isRisky) ||
			Boolean(_.get(countriesObj, beneficiary?.bankAccountCountryId || 0)?.isRisky),
		[beneficiary?.countryId, beneficiary?.bankAccountCountryId]
	);

	const needCommentForUnequalCountries = useMemo(
		() => beneficiary?.bankAccountCountryId && beneficiary?.bankAccountCountryId !== beneficiary?.countryId,
		[beneficiary?.countryId, beneficiary?.bankAccountCountryId]
	);

	const canReEditPaymentsScheme = useMemo(
		() =>
			[
				BeneficiaryStatus.Approved,
				BeneficiaryStatus.ApprovedWithRisk,
				BeneficiaryStatus.PendingBankAccountDetails,
				BeneficiaryStatus.DeclinedForRepair,
			].includes(beneficiary.status as BeneficiaryStatus),
		[beneficiary.status]
	);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		dispatch(setBeneficiary({ name: name, value: value }));
		if (_.has(fieldsValidations, name)) {
			setInvalidFields(setListOfInvalids(invalidFields, fieldsValidations, name, value));
		}
		if (_.has(commentForUnequalCountriesValidation, name)) {
			setInvalidFields(setListOfInvalids(invalidFields, commentForUnequalCountriesValidation, name, value));
		}
	};

	const handlePaymentCategoriesChange = (selected: string[]) => {
		const normalized = selected || [];
		dispatch(
			setBeneficiary({
				name: 'paymentCategories',
				value: normalized,
			})
		);
		if (normalized.length) {
			setInvalidFields((prev) => {
				const next = { ...prev };
				delete next['paymentCategories'];
				return next;
			});
		}
	};

	useEffect(() => {
		if (
			beneficiary.countryId &&
			beneficiary.bankAccountCountryId &&
			beneficiary.currencyId &&
			props.isCountryChanged
		) {
			dispatch(getPaymentsOptions(beneficiary, true, true));
		}
	}, []);

	useEffect(() => {
		if (isPaymentOptionsPreloaded && Object.keys(paymentOptions).length && beneficiary && isNewBeneficiary) {
			let paymentOptionsIncludedKeys = _.keys(paymentOptions).filter(
				(key) =>
					(key.includes(paymentsMainTypes.REGULAR) && beneficiary.isLocalPaymentAllowed) ||
					(key.includes(paymentsMainTypes.PRIORITY) && beneficiary.isPriorityPaymentAllowed)
			);

			let filteredPaymentOptionsIncludedKeys: any = {};

			_.forEach(paymentOptionsIncludedKeys, (option) => {
				const optionFields = _.get(paymentOptions[option], 'fields');
				_.forEach(optionFields, (field: any) => {
					const key = _.get(paymentsMethodsMapping, field);
					if (!beneficiary[key]) {
						filteredPaymentOptionsIncludedKeys[`${option}`] = false;
						return false;
					} else {
						filteredPaymentOptionsIncludedKeys[`${option}`] = true;
					}
				});
			});

			const paymentOptionsObj = _.keys(paymentOptions).reduce(
				(a, key) => Object.assign(a, { [key]: { ...paymentOptions[key] } }),
				{}
			);

			paymentOptionsIncludedKeys?.forEach((key) => {
				paymentOptionsObj[key] = {
					...paymentOptions[key],
					optional: filteredPaymentOptionsIncludedKeys[key] ? true : false,
				};
			});

			dispatch(
				setBeneficiary({
					name: 'paymentsOptions',
					value: {
						...paymentOptionsObj,
					},
				})
			);
			dispatch(setOptionalPayments);
		}
	}, [paymentOptions]);

	const formProps = {
		isOptional: isOptional,
		handleChange: handleChange,
		setOptionalPayments: (payment: paymentsTypes, value: boolean) => {
			let paymentOptionsIncludedKeys = _.keys(paymentOptions);
			if (payment.includes(paymentsMainTypes.REGULAR)) {
				paymentOptionsIncludedKeys = paymentOptionsIncludedKeys.filter((option) =>
					option.includes(paymentsMainTypes.REGULAR)
				);
			} else {
				paymentOptionsIncludedKeys = paymentOptionsIncludedKeys.filter((option) =>
					option.includes(paymentsMainTypes.PRIORITY)
				);
			}

			const paymentOptionsObj = _.keys(paymentOptions).reduce(
				(a, key) => Object.assign(a, { [key]: { ...paymentOptions[key] } }),
				{}
			);

			dispatch(
				setBeneficiary({
					name: 'paymentsOptions',
					value: {
						...paymentOptionsObj,
						[payment]: { ...paymentOptions[payment], optional: value },
					},
				})
			);
		},
		optionalPayments: paymentOptions,
	};

	useEffect(() => {
		// Only fetch if categories are not already loaded (e.g., when creating new beneficiary)
		// When editing, categories are prefetched in the parent component
		if (!categoryOptions || categoryOptions.length === 0) {
			getPaymentCategories()
				.then((cats) => dispatch(setPaymentCategoryOptions(cats || [])))
				.catch(() => dispatch(setPaymentCategoryOptions([])));
		}
	}, []);

	const hasValidPayment = useMemo(() => {
		if (formProps.isOptional) {
			let selected = false;
			_.keys(paymentOptions).forEach((key) => {
				selected = paymentOptions[key].optional ? true : selected;
			});
			return selected;
		}
		return _.keys(paymentOptions).length > 0 ? true : false;
	}, [beneficiary]);

	const isValidForm = useMemo(() => {
		//check fields
		let obj = {};

		BeneficiaryDetailsBankAccount.forEach((element) => {
			obj[element] = String(beneficiary[element] || '');
		});
		obj['formalBankApproveFile'] = beneficiary?.formalBankApproveFile?.originalFileName;
		obj['foreignTradeAgreement'] = beneficiary?.foreignTradeAgreement;
		obj['paymentCategories'] = (beneficiary?.paymentCategories || []).join(',');

		_.keys(paymentOptions).forEach((key) => {
			if (paymentOptions[key].optional) {
				paymentOptions[key].fields.forEach((field) => {
					switch (field) {
						case 'acct_number':
							obj['acctNumber'] = beneficiary.acctNumber;
							break;
						case 'bic_swift':
							obj['bicSwift'] = beneficiary.bicSwift;
							break;
						default:
							obj[field] = beneficiary[field];
					}
					obj[field] = beneficiary[field];
				});
			}
		});
		const validateFormalBankApproveFile = needFormalBankApprove ? formalBankApproveFileValidation : {};

		const validateCommentForUnequalCountries = needCommentForUnequalCountries
			? commentForUnequalCountriesValidation
			: {};

		const filesValidation = {};
		if (!beneficiary.files || beneficiary.files.length === 0) {
			filesValidation['files'] = true;
		}
		const fieldValidations =
			validateFields(
				{
					...fieldsValidations,
					...validateFormalBankApproveFile,
					...validateCommentForUnequalCountries,
				},
				obj
			) || {};

		return {
			...fieldValidations,
			...filesValidation,
		};
	}, [beneficiary]);

	const validateStep = () => {
		dispatch(setMessage({ ...userMessage, exist: false }));
		const invalidFields = isValidForm;
		if (!_.keys(invalidFields).length) {
			const bn = prepareBeneficiaryForCreation(beneficiary, paymentOptions);
			dispatch(validateStep2(bn));
		} else {
			dispatch(
				setMessage({
					exist: true,
					type: userMesaageType.ERROR,
					text: 'missing payment',
				})
			);
			setInvalidFields(invalidFields);
		}
	};
	const [invalidFields, setInvalidFields] = useState<invalidFields>({});

	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				title={t(userMessage.text || '')}
				subTitle={userMessage.subText}
				handleCancel={() => {
					dispatch(setMessage({ exist: false }));
				}}
			/>
			<FormsStack>
				<BankAccountDetailsForm
					invalidFields={invalidFields}
					setInvalidFields={setInvalidFields}
					handleChange={handleChange}
					isCountryDisabled={isPaymentOptionsPreloaded}
				/>
				<InfoCard>
					<Stack spacing={2}>
						<Typography variant="h3" pb={1}>
							{t('page.beneficiary.create.step.3.payment_categories.title')}
						</Typography>
						<Typography variant="body2" pb={1}>
							{t('page.beneficiary.create.step.2.payment_categories.description')}
						</Typography>
						<MultiPaymentCategoryPicker
							value={(beneficiary.paymentCategories || []).map((cat) =>
								typeof cat === 'string' ? cat : cat.id
							)}
							onChange={handlePaymentCategoriesChange}
							disabled={false}
							label={t('page.beneficiary.create.step.2.payment_categories.label')}
							required={true}
							options={categoryOptions}
							hideTitle={true}
						/>
					</Stack>
				</InfoCard>
				<Fragment>
					{
						<>
							{_.keys(paymentOptions).map((key, inx) => {
								return (
									<FormWrap
										key={inx}
										{...formProps}
										payment={key}
										disabled={isPaymentOptionsPreloaded && !canReEditPaymentsScheme}
									>
										<LocalPaymentForm
											payment={paymentOptions[key].fields}
											invalidFields={invalidFields}
											handleChange={handleChange}
											disabled={isPaymentOptionsPreloaded && !canReEditPaymentsScheme}
										/>
									</FormWrap>
								);
							})}
						</>
					}
				</Fragment>
				{needFormalBankApprove ? (
					<TaxShelterForm
						invalidFields={invalidFields}
						setInvalidFields={setInvalidFields}
						handleChange={handleChange}
						isCountryDisabled={isPaymentOptionsPreloaded}
					/>
				) : (
					<></>
				)}
				<NotesForm
					invalidFields={invalidFields}
					setInvalidFields={setInvalidFields}
					handleChange={handleChange}
					isCountryDisabled={isPaymentOptionsPreloaded}
				/>
				<>
					{_.includes([UserPermission.SIGNER, UserPermission.OPERATIONER], userPermission) && (
						<InfoCard>
							<FormHeadWithCheckbox>
								<Stack flexDirection={'row'} gap={1}>
									<CustomCheckbox>
										<Checkbox
											sx={{ padding: 0, alignItems: 'start' }}
											onChange={(event) => {
												dispatch(
													setBeneficiary({
														name: 'foreignTradeAgreement',
														value: event.target.checked,
													})
												);
											}}
											checked={beneficiary?.foreignTradeAgreement || false}
										/>
									</CustomCheckbox>
									<Typography fontFamily="body4" variant="body1">
										{t(`${prefixFields}disclaimer.title`)}
									</Typography>
								</Stack>
							</FormHeadWithCheckbox>
						</InfoCard>
					)}
				</>
				<Footer
					handleCancel={props.handleCancel}
					handleBack={() => {
						if (beneficiary?.backupBeneficiaryId) {
							dispatch(deleteBeneficiary(beneficiary.id!, true));
							dispatch(
								upsertBeneficiary({
									...beneficiary,
									id: beneficiary.backupBeneficiaryId,
									backupBeneficiaryId: null,
								})
							);
						}
						dispatch(setActiveStep(beneficiarySteps.BENEFICIARY));
					}}
					ifBack={true}
					disableSubmit={_.keys(isValidForm).length === 0 && hasValidPayment ? false : true}
					handleSubmit={validateStep}
					labelBack={t(`${prefixFields}back_button.text`)}
					labelSubmit={t(`${prefixFields}validate_button.text`)}
					labelCancel={t(`${prefixFields}cancel_button.text`)}
				/>
			</FormsStack>
		</Fragment>
	);
}
